<script setup>
const { $tp } = useNuxtApp();
</script>

<template>
  <section id="solutions" class="sections-solutions">
    <div class="sections-solutions__container container">
      <div
        class="sections-solutions__radial sections-solutions__radial--1"
      ></div>

      <div
        class="sections-solutions__radial sections-solutions__radial--2"
      ></div>

      <div class="sections-solutions__body">
        <CSpriteIcon
          v-for="item in 6"
          :key="item"
          class="sections-solutions__icon sections-solutions__icon--plus"
          :class="`sections-solutions__icon--plus--${item}`"
          name="plus"
        />

        <UiCaptionUnderline
          caption="solutions"
          class="sections-solutions__caption"
        />

        <h2 class="sections-solutions__title">
          <span
            class="sections-solutions__font sections-solutions__font--title"
          >
            {{ $tp('Solutions for Travel, Built for Real Journeys') }}
          </span>
        </h2>

        <div class="sections-solutions__solutions">
          <div class="sections-solutions__solutions-part">
            <BlockFeature
              size="md"
              class="sections-solutions__solution"
              sprite-icon="icon_sol_back-office"
              title="Smart Backoffice & Management"
              text="Easy-to-use back-office systems designed to effortlessly manage transport and logistics. Our systems simplify scheduling, fleet management, route planning, and cost tracking.<br/> Handle your transport operations with clarity and control."
            />

            <BlockFeature
              size="md"
              class="sections-solutions__solution"
              sprite-icon="icon_sol_tracking"
              title="Live Location Tracking & Route Management"
              text="Know exactly where every object is, in real time. Our interactive maps and live data tools help to stay aligned, bringing clarity to every moment."
            />

            <BlockFeature
              size="md"
              class="sections-solutions__solution"
              sprite-icon="icon_sol_support"
              title="Communication & Support&nbsp;Tools"
              text="Connect with your customers at every touchpoint. From automated notifications to live chat and ticketing systems, our tools make communication fast, easy, and reliable. Communication matters"
            />
          </div>

          <div class="sections-solutions__solutions-part">
            <BlockFeature
              size="md"
              class="sections-solutions__solution"
              sprite-icon="icon_sol_monitoring"
              title="Dynamic Price & Availability Monitoring"
              text="Stay one step ahead with real-time reviewing of prices and seat availability. Our algorithms track changes continuously, giving you actionable insights and alerts. Perfect for dynamic pricing strategies, demand forecasting, and analysis."
            />

            <BlockFeature
              size="md"
              class="sections-solutions__solution"
              sprite-icon="icon_sol_tickets"
              title="Online Booking & Reservation Platforms"
              text="A seamless booking experience across devices. Our solutions support custom workflows, seat selection, secure payments, and multi-language interfaces. Built for both direct sales and agency distribution."
            />

            <BlockFeature
              size="md"
              class="sections-solutions__solution"
              sprite-icon="icon_sol_loyalty"
              title="Loyalty &<br/> Engagement&nbsp;Systems"
              text="Turn passengers into repeat customers with flexible loyalty programs. Reward behavior, track points, and offer personalized incentives through a system that integrates directly with the booking and CRM layers. Designed to grow lifetime value and deepen brand connection."
            />
          </div>
        </div>

        <CPicture
          name="centurio_solutions"
          class="sections-solutions__image sections-solutions__image--solutions"
        />
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.sections-solutions {
  overflow: hidden;

  &__font {
    display: block;

    &--title {
      font-size: em(30);
      font-weight: 800;
      line-height: 119%;

      @include media-breakpoint-down(md) {
        font-size: em(24);
      }

      @include media-breakpoint-down(sm) {
        font-size: em(20);
      }
    }
  }

  &__icon {
    &--plus {
      position: absolute;
      width: em(40);
      height: em(40);
      pointer-events: none;
      opacity: 0.5;

      &--1 {
        top: 0;
        left: 0;

        @include media-breakpoint-down(md) {
          top: em(-4);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: em(-5);
          left: em(-10);
        }
      }

      &--2 {
        top: em(60);
        left: 0;

        @include media-breakpoint-down(md) {
          top: em(44);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: em(39);
          left: em(-10);
        }
      }

      &--3 {
        top: em(174);
        left: 0;

        @include media-breakpoint-down(md) {
          top: em(134);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: em(113);
          left: em(-10);
        }
      }

      &--4 {
        top: 0;
        right: 0;

        @include media-breakpoint-down(md) {
          top: em(-4);
          right: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: em(-5);
          right: em(-10);
        }
      }

      &--5 {
        top: em(60);
        right: 0;

        @include media-breakpoint-down(md) {
          top: em(44);
          right: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: em(39);
          right: em(-10);
        }
      }

      &--6 {
        top: em(174);
        right: 0;

        @include media-breakpoint-down(md) {
          top: em(134);
          right: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: em(113);
          right: em(-10);
        }
      }
    }
  }

  &__container {
    position: relative;

    @include media-breakpoint-down(md) {
      max-width: em(480);
    }
  }

  &__radial {
    position: absolute;
    opacity: 0.7;

    &--1 {
      top: em(-375);
      left: em(230);
      width: em(1078);
      height: em(727);
      background: radial-gradient(
        50% 50% at 50% 50%,
        #ffffff 0%,
        rgb(255 255 255 / 0%) 100%
      );

      @include media-breakpoint-down(md) {
        top: em(-100);
        left: em(130);
        width: em(757);
        height: em(832);
      }

      @include media-breakpoint-down(sm) {
        top: em(140);
        left: em(40);
        width: em(605);
        height: em(665);
      }
    }

    &--2 {
      top: em(-50);
      left: em(81);
      width: em(946);
      height: em(1040);
      background: radial-gradient(
        50% 50% at 50% 50%,
        #ffffff 0%,
        rgb(255 255 255 / 0%) 100%
      );

      @include media-breakpoint-down(md) {
        display: none;
      }
    }
  }

  &__body {
    position: relative;
    padding: em(40) em(40) em(48);

    @include media-breakpoint-down(md) {
      padding: em(33) em(32) em(58);
    }

    @include media-breakpoint-down(sm) {
      padding: em(29) em(21) em(49);
    }
  }

  &__caption {
  }

  &__title {
    width: em(400);
    margin-top: em(42);
    color: $text-dark-main;

    @include media-breakpoint-down(md) {
      width: auto;
      max-width: em(300);
      margin-top: em(34);
    }

    @include media-breakpoint-down(sm) {
      max-width: em(250);
      margin-top: em(28);
    }
  }

  &__solutions {
    display: flex;
    justify-content: space-between;
    margin-top: em(82);

    @include media-breakpoint-down(md) {
      flex-direction: column;
      margin-top: em(60);
    }

    @include media-breakpoint-down(sm) {
      flex-direction: column;
      margin-top: em(50);
    }
  }

  &__solutions-part {
    width: em(230);

    &:not(:last-child) {
      @include media-breakpoint-down(md) {
        margin-bottom: em(50);
      }

      @include media-breakpoint-down(sm) {
        margin-bottom: em(24);
      }
    }

    @include media-breakpoint-down(md) {
      width: 100%;
      max-width: em(288);
    }

    @include media-breakpoint-down(sm) {
      max-width: em(229);
    }
  }

  &__solution {
    &:not(:last-child) {
      margin-bottom: em(40);

      @include media-breakpoint-down(md) {
        margin-bottom: em(23);
      }
    }
  }

  &__image {
    &--solutions {
      position: absolute;
      bottom: em(-139);
      left: 50%;
      width: em(440);
      height: auto;
      transform: translateX(-50%);

      @include media-breakpoint-down(md) {
        right: em(-268);
        bottom: em(228);
        left: unset;
        width: em(344);
        transform: none;
      }

      @include media-breakpoint-down(sm) {
        right: em(-179);
        bottom: em(480);
        left: unset;
        width: em(275);
        transform: none;
      }
    }
  }
}
</style>
